<!--
 * @Author: LiZhiWei
 * @Date: 2026-08-12 10:00:00
 * @Description: 站点公共页脚：品牌简介 + 栏目入口 + 联系信息
-->
<template>
  <footer class="site-footer">
    <div class="page-container">
      <div class="footer-grid">
        <div class="footer-brand-col">
          <div class="footer-brand">
            <img class="footer-logo" src="/logo/xingke-logo.png" alt="" width="52" height="52" />
            <span>{{ companyIdentity.name }}</span>
          </div>
          <p class="footer-intro">{{ companyIdentity.intro }}</p>
        </div>

        <nav
          v-for="col in footerColumns"
          :key="col.title"
          class="footer-col"
          :aria-label="col.title"
        >
          <h3>{{ col.title }}</h3>
          <template v-for="link in col.links" :key="link.label">
            <contact-dialog v-if="'action' in link" default-type="合作咨询">
              <button class="footer-link-button" type="button">{{ link.label }}</button>
            </contact-dialog>
            <nuxt-link
              v-else
              :to="link.to"
              :target="link.external ? '_blank' : undefined"
              :rel="link.external ? 'noopener' : undefined"
            >
              {{ link.label }}
            </nuxt-link>
          </template>
        </nav>

        <div class="footer-col footer-contact-col">
          <h3>联系信息</h3>
          <div class="footer-phone-list" aria-label="业务联系电话">
            <a
              v-for="item in footerPhoneContacts"
              :key="item.type"
              class="footer-phone-item"
              :href="`tel:${item.phone}`"
              :aria-label="`${item.type}电话 ${item.phone}`"
            >
              <span class="footer-phone-type">{{ item.type }}</span>
              <span class="footer-phone-number">{{ item.phone }}</span>
            </a>
          </div>
          <div class="footer-contact-details">
            <dl class="footer-contact-meta">
              <div>
                <dt>地址</dt>
                <dd>{{ contactInfo.address }}</dd>
              </div>
              <div>
                <dt>邮箱</dt>
                <dd>
                  <a :href="`mailto:${contactInfo.email}`">{{ contactInfo.email }}</a>
                </dd>
              </div>
            </dl>
            <nuxt-link
              class="footer-external"
              :to="externalLinks.botanicalGarden"
              target="_blank"
              rel="noopener"
              :aria-label="`访问${externalLinks.botanicalGardenLabel}官网（新窗口打开）`"
            >
              <span class="footer-external-label">
                访问{{ externalLinks.botanicalGardenLabel }}官网
              </span>
              <span class="external-mark" aria-hidden="true">↗</span>
            </nuxt-link>
          </div>
        </div>
      </div>

      <div class="footer-bottom">
        <span>© {{ year }} {{ companyIdentity.name }}</span>
        <a
          class="footer-icp"
          :href="filingInfo.icpLink"
          target="_blank"
          rel="noopener"
          :aria-label="`${filingInfo.icp}（工信部备案管理系统，新窗口打开）`"
        >
          {{ filingInfo.icp }}
        </a>
      </div>
    </div>
  </footer>
</template>

<script setup lang="ts">
  import type { FooterColumn, FooterLink } from '~/utils/site'

  import { getArticleCategoryEntryRoute, getMediaCategoryEntryRoute } from '~/utils/content'
  import { companyIdentity, contactInfo, externalLinks, filingInfo } from '~/utils/site'

  defineOptions({
    name: 'MFooter',
  })

  const footerPhoneContacts = [
    { type: '合作', phone: '089862555670' },
    { type: '采购', phone: '089862556030' },
    { type: '研学', phone: '19989119251' },
  ]

  const { data: categoryResponse } = useArticleCategories()
  const articleFooterLinks = computed<FooterLink[]>(() =>
    (categoryResponse.value?.data ?? []).map((category) => ({
      label: category.name,
      to: getArticleCategoryEntryRoute(category).path,
    }))
  )
  const { categoryTree: mediaCategoryTree } = useMediaCategories()
  const mediaFooterLinks = computed<FooterLink[]>(() =>
    mediaCategoryTree.value.map((category) => ({
      label: category.name,
      to: getMediaCategoryEntryRoute(category).path,
    }))
  )
  const footerColumns = computed<FooterColumn[]>(() => [
    {
      title: '认识兴科',
      links: [
        { label: '企业简介', to: '/company/profile' } as FooterLink,
        { label: '科研与产业实力', to: '/company/strength' } as FooterLink,
        { label: '资质证书', to: '/company/qualifications' } as FooterLink,
      ],
    },
    {
      title: '内容入口',
      links: [...articleFooterLinks.value, ...mediaFooterLinks.value],
    },
  ])

  const year = new Date().getFullYear()
</script>

<style scoped lang="scss">
  .site-footer {
    padding: 44px 0 22px;
    color: rgb(255 255 255 / 76%);
    text-align: left;
    background: var(--footer-bg);
  }

  .footer-grid {
    display: grid;
    grid-template-columns: 1.15fr 0.72fr 0.72fr 1.45fr;
    gap: 32px;
  }

  .footer-brand {
    display: flex;
    gap: 12px;
    align-items: center;
    font-weight: 620;
    color: #fff;
  }

  .footer-logo {
    flex: 0 0 52px;
    width: 52px;
    height: 52px;
    object-fit: contain;
    background: #fff;
    border-radius: 50%;
  }

  .footer-intro {
    width: calc(100% - 12px);
    max-width: none;
    margin: 16px 0 0;
    font-size: 13px;
    color: inherit;
  }

  .footer-col h3 {
    margin: 0 0 12px;
    font-size: 14px;
    color: #fff;
  }

  .footer-col > a,
  .footer-col > span,
  .footer-link-button {
    display: block;
    width: 100%;
    padding: 4px 0;
    font-size: 13px;
    line-height: inherit;
    color: inherit;
    text-align: left;
    background: transparent;
    border: 0;
  }

  .footer-col > a,
  .footer-link-button {
    cursor: pointer;
  }

  .footer-col > a:hover,
  .footer-link-button:hover {
    color: #fff;
  }

  .footer-contact-col {
    min-width: 0;
  }

  .footer-phone-list {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 18px;
  }

  .footer-phone-item {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
    color: inherit;
    cursor: pointer;
    transition: color 180ms var(--ease);

    &:hover {
      color: #fff;
    }
  }

  .footer-phone-type {
    font-size: 13px;
    color: rgb(255 255 255 / 52%);
  }

  .footer-phone-number {
    font: 600 13px/1.4 var(--num);
    font-variant-numeric: tabular-nums;
    color: #fff;
    overflow-wrap: anywhere;
  }

  .footer-contact-meta {
    display: flex;
    flex-direction: column;
    gap: 2px;
    margin: 0;

    > div {
      display: flex;
      gap: 6px;
      align-items: start;
    }

    dt,
    dd {
      margin: 0;
      font-size: 12px;
      line-height: 1.7;
    }

    dt {
      color: rgb(255 255 255 / 52%);
    }

    dd,
    a {
      min-width: 0;
      color: inherit;
      overflow-wrap: anywhere;
    }

    a {
      cursor: pointer;

      &:hover {
        color: #fff;
      }
    }
  }

  .footer-contact-details {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 8px 20px;
    align-items: center;
    margin-top: 10px;
  }

  .footer-contact-details > .footer-external {
    display: inline-flex;
    gap: 6px;
    align-items: center;
    width: auto;
    padding: 4px 0 2px;
    margin-top: 2px;
    font-size: 12px;
    font-weight: 600;
    color: rgb(255 255 255 / 90%);

    &:hover {
      color: #fff;

      .footer-external-label::after {
        transform: scaleX(1);
      }

      .external-mark {
        transform: translate(2px, -2px);
      }
    }
  }

  .footer-external-label {
    position: relative;

    &::after {
      position: absolute;
      right: 0;
      bottom: -2px;
      left: 0;
      height: 1px;
      content: '';
      background: currentColor;
      transform: scaleX(0);
      transform-origin: left;
      transition: transform 180ms var(--ease);
    }
  }

  .external-mark {
    display: inline-block;
    font-size: 13px;
    line-height: 1;
    color: currentColor;
    transition: transform 180ms var(--ease);
  }

  .footer-bottom {
    display: flex;
    gap: 20px;
    justify-content: space-between;
    padding-top: 14px;
    margin-top: 28px;
    font-size: 12px;
    border-top: 1px solid rgb(255 255 255 / 14%);
  }

  .footer-icp {
    color: inherit;
    cursor: pointer;
    transition: color 180ms var(--ease);

    &:hover {
      color: #fff;
    }
  }

  @media (max-width: 1180px) {
    .footer-grid {
      grid-template-columns: 1.3fr 1fr 1fr;
    }

    .footer-contact-col {
      grid-column: 2 / 4;
    }
  }

  @media (max-width: 767px) {
    .site-footer {
      padding: 36px 0 20px;
    }

    .footer-grid {
      grid-template-columns: 1fr 1fr;
      gap: 24px 28px;
    }

    .footer-brand-col {
      grid-column: 1 / -1;
    }

    .footer-contact-col {
      grid-column: 1 / -1;
    }

    .footer-contact-details {
      grid-template-columns: minmax(0, 1fr);
      gap: 8px;
    }

    .footer-contact-details > .footer-external {
      grid-column: auto;
    }

    .footer-bottom {
      flex-direction: column;
      gap: 6px;
      margin-top: 22px;
    }
  }

  @media (max-width: 430px) {
    .footer-phone-number {
      font-size: 12px;
    }
  }

  :focus-visible {
    outline: 3px solid color-mix(in oklch, var(--brand), white 35%);
    outline-offset: 2px;
  }
</style>
